import React, { useState, useEffect, useRef } from 'react';
import { Lightbulb, Leaf, TrendingUp, Zap, Heart } from 'lucide-react';

const AboutPage = ({ content, language }) => {
  const [sectionVisible, setSectionVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight - offset) && rect.bottom >= offset;
    };
    
    const handleScroll = () => {
      Object.keys(sectionRefs.current).forEach(key => {
        if (sectionRefs.current[key] && isInViewport(sectionRefs.current[key])) {
          setSectionVisible(prev => ({ ...prev, [key]: true }));
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      {/* Company Overview */}
      <div 
        ref={el => sectionRefs.current['overview'] = el}
        className="max-w-6xl mx-auto px-6 md:px-12 py-16"
      >
        <h1 className={`text-5xl font-bold mb-8 text-center transition-all duration-700 ${sectionVisible.overview ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          {language === 'en' ? 'About Infinity Development' : 'Acerca de Infinity Development'}
        </h1>
        <div className={`h-1 w-24 bg-gradient-to-r from-blue-500 to-green-500 mx-auto mb-16 transition-all duration-700 delay-200 ${sectionVisible.overview ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
        
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className={`text-lg text-gray-300 leading-relaxed transition-all duration-700 delay-300 ${sectionVisible.overview ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              {language === 'en' 
                ? 'Infinity Development is a forward-thinking real estate development firm building the next generation of regenerative, intelligent communities.'
                : 'Infinity Development es una firma de desarrollo inmobiliario visionaria que construye la próxima generación de comunidades regenerativas e inteligentes.'}
            </p>
            <p className={`text-lg text-gray-300 leading-relaxed transition-all duration-700 delay-400 ${sectionVisible.overview ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              {language === 'en'
                ? 'We operate at the intersection of innovation, sustainability, and long-term impact — structuring high-conviction projects across urban infill, mixed-use, and international markets. Our model merges startup speed with development precision, leveraging AI, circular design, and cross-border capital to reshape how cities evolve.'
                : 'Operamos en la intersección de innovación, sostenibilidad e impacto a largo plazo — estructurando proyectos de alta convicción en relleno urbano, uso mixto y mercados internacionales. Nuestro modelo fusiona la velocidad de startup con la precisión de desarrollo, aprovechando IA, diseño circular y capital transfronterizo para remodelar cómo evolucionan las ciudades.'}
            </p>
          </div>
          <div className={`bg-gray-900 bg-opacity-30 rounded-lg min-h-[300px] flex items-center justify-center transition-all duration-700 delay-500 ${sectionVisible.overview ? 'opacity-100 transform-none' : 'opacity-0 translate-x-10'}`}>
            <img 
              src="black-icon.jpg" 
              alt="Infinity Development Logo" 
              className="max-w-full max-h-full object-contain rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <p className="text-gray-500 italic hidden">Company Image</p>
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div 
        ref={el => sectionRefs.current['mission'] = el}
        className="max-w-6xl mx-auto px-6 md:px-12 py-16"
      >
        <div className="grid md:grid-cols-2 gap-12">
          <div className={`bg-gray-900 bg-opacity-30 p-8 rounded-lg transition-all duration-700 ${sectionVisible.mission ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-3xl font-bold mb-6">
              {language === 'en' ? 'Our Mission' : 'Nuestra Misión'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {language === 'en'
                ? 'To transform bold ideas into regenerative infrastructure that benefits both communities and investors — through innovation, sustainability, and intention.'
                : 'Transformar ideas audaces en infraestructura regenerativa que beneficie tanto a las comunidades como a los inversionistas — a través de la innovación, sostenibilidad e intención.'}
            </p>
          </div>
          <div className={`bg-gray-900 bg-opacity-30 p-8 rounded-lg transition-all duration-700 delay-200 ${sectionVisible.mission ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-3xl font-bold mb-6">
              {language === 'en' ? 'Our Vision' : 'Nuestra Visión'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {language === 'en'
                ? 'To build Infinite Cities: communities that regenerate economically, socially, and ecologically over time.'
                : 'Construir Ciudades Infinitas: comunidades que se regeneran económica, social y ecológicamente a través del tiempo.'}
            </p>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div 
        ref={el => sectionRefs.current['values'] = el}
        className="max-w-6xl mx-auto px-6 md:px-12 py-16"
      >
        <h2 className={`text-4xl font-bold mb-16 text-center transition-all duration-700 ${sectionVisible.values ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          {language === 'en' ? 'Core Values' : 'Valores Fundamentales'}
        </h2>
        <div className="grid md:grid-cols-3 gap-10">
          {/* Innovation */}
          <div 
            className={`text-center transition-all duration-700 ${sectionVisible.values ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mb-6 transition-all duration-500 hover:from-blue-600 hover:to-blue-700">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4 uppercase">
              {language === 'en' ? 'Innovation' : 'Innovación'}
            </h3>
            <div className="h-0.5 w-16 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto my-3"></div>
            <p className="text-gray-300 leading-relaxed">
              {language === 'en'
                ? 'We integrate emerging technologies and automation to make development smarter, faster, and more impactful.'
                : 'Integramos tecnologías emergentes y automatización para hacer el desarrollo más inteligente, rápido e impactante.'}
            </p>
          </div>

          {/* Sustainability */}
          <div 
            className={`text-center transition-all duration-700 ${sectionVisible.values ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-green-500 to-green-600 mb-6 transition-all duration-500 hover:from-green-600 hover:to-green-700">
              <Leaf className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4 uppercase">
              {language === 'en' ? 'Sustainability' : 'Sostenibilidad'}
            </h3>
            <div className="h-0.5 w-16 bg-gradient-to-r from-green-500 to-green-600 mx-auto my-3"></div>
            <p className="text-gray-300 leading-relaxed">
              {language === 'en'
                ? 'Every decision is guided by environmental responsibility and a commitment to regenerative urbanism.'
                : 'Cada decisión está guiada por la responsabilidad ambiental y un compromiso con el urbanismo regenerativo.'}
            </p>
          </div>

          {/* Purpose-Driven Capital */}
          <div 
            className={`text-center transition-all duration-700 ${sectionVisible.values ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '400ms' }}
          >
            <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 mb-6 transition-all duration-500 hover:from-purple-600 hover:to-purple-700">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4 uppercase">
              {language === 'en' ? 'Purpose-Driven Capital' : 'Capital con Propósito'}
            </h3>
            <div className="h-0.5 w-16 bg-gradient-to-r from-purple-500 to-purple-600 mx-auto my-3"></div>
            <p className="text-gray-300 leading-relaxed">
              {language === 'en'
                ? 'We align with capital partners who value long-term prosperity, impact, and stewardship.'
                : 'Nos alineamos con socios de capital que valoran la prosperidad a largo plazo, el impacto y la administración responsable.'}
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 text-center">
        <h2 className="text-4xl font-bold mb-8 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">
          {language === 'en' ? 'JOIN OUR MISSION' : 'ÚNETE A NUESTRA MISIÓN'}
        </h2>
        <p className="text-xl font-light italic max-w-2xl mx-auto mb-10 text-gray-300">
          {language === 'en' 
            ? '"Transform. Regenerate. Impact."' 
            : '"Transformar. Regenerar. Impactar."'}
        </p>
        
        <div className="mt-12">
          <button className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-4 text-xl font-medium hover:from-blue-700 hover:to-green-700 transition-all duration-300 rounded transform hover:scale-105">
            {language === 'en' ? 'PARTNER WITH US' : 'ASÓCIATE CON NOSOTROS'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;